import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { apiFetch } from '@/lib/apiFetch'
import MagnumSays from '@/components/MagnumSays'
import ActLogList from '@/components/act/ActLogList'
import ActPaceTable from '@/components/act/ActPaceTable'
import ActStrategyTable from '@/components/act/ActStrategyTable'
import ActSummaryTiles, { CalibrationGrid } from '@/components/act/ActSummaryTiles'
import {
  ActCatalog,
  ActInsights,
  ActSessionReview,
  ActSummary,
  browserTimezone,
  fetchCatalog,
  formatDuration,
  percent,
} from '@/lib/actModes'
import { cn } from '@/lib/utils'

interface SessionRow {
  sessionId: number
  exerciseType: string | null
  startedAt: number
  endedAt: number | null
  summary: ActSummary
}

const WINDOWS = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
]

/** Magnus's read on the window, in one line: pace first, since pace is what decides the section. */
function headline(insights: ActInsights): string {
  const summary = insights.summary
  const behind = insights.pace.filter(p => p.avgSecondsPerQuestion != null
    && p.avgSecondsPerQuestion > p.testSecondsPerQuestion)

  const opener = `${summary.questionsCompleted} questions, ${percent(summary.accuracy)} right, ${formatDuration(summary.avgActualSeconds)} apiece.`
  if (behind.length === 0 && insights.pace.some(p => p.avgSecondsPerQuestion != null)) {
    return `${opener} You're inside test pace everywhere you've logged. Now hold it for a whole section.`
  }
  if (behind.length > 0) {
    const worst = behind.sort((a, b) =>
      (b.avgSecondsPerQuestion! - b.testSecondsPerQuestion) - (a.avgSecondsPerQuestion! - a.testSecondsPerQuestion))[0]
    return `${opener} ${worst.label} is where the clock is beating you — ${formatDuration(worst.avgSecondsPerQuestion)} a question against ${formatDuration(worst.testSecondsPerQuestion)}.`
  }
  return opener
}

/**
 * His numbers. Pace against the real clock first, then what he actually got
 * right -- everything else is one tap away rather than stacked on the screen.
 */
export default function ActReviewPage() {
  const { user } = useAuth()
  const [catalog, setCatalog] = useState<ActCatalog | null>(null)
  const [days, setDays] = useState(30)
  const [insights, setInsights] = useState<ActInsights | null>(null)
  const [sessions, setSessions] = useState<SessionRow[]>([])
  const [openSession, setOpenSession] = useState<number | null>(null)
  const [sessionReview, setSessionReview] = useState<ActSessionReview | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchCatalog().then(setCatalog).catch(() => {})
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([
      apiFetch<ActInsights>(`/act/insights?days=${days}&timezone=${encodeURIComponent(browserTimezone())}`),
      apiFetch<{ sessions: SessionRow[] }>('/act/sessions?limit=100'),
    ])
      .then(([insightData, sessionData]) => {
        if (cancelled) return
        setInsights(insightData)
        setSessions(sessionData.sessions || [])
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [days])

  const openSessionDetail = async (sessionId: number) => {
    if (openSession === sessionId) {
      setOpenSession(null)
      setSessionReview(null)
      return
    }
    setOpenSession(sessionId)
    setSessionReview(null)
    try {
      setSessionReview(await apiFetch<ActSessionReview>(`/act/sessions/${sessionId}/review`))
    } catch {}
  }

  if (!user || user.type !== 'child') return null

  const summary = insights?.summary

  return (
    <div className="max-w-2xl mx-auto space-y-6 py-2">
      <div className="flex items-center justify-between">
        <Link to="/practice/act" className="text-sm text-muted-foreground hover:text-foreground transition">
          &larr; Back
        </Link>
        <div className="flex gap-2">
          {WINDOWS.map(w => (
            <button
              key={w.days}
              onClick={() => setDays(w.days)}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-medium transition min-h-[36px]',
                days === w.days
                  ? 'bg-primary text-primary-foreground'
                  : 'border border-border text-muted-foreground hover:bg-accent'
              )}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-baseline justify-between gap-3">
        <h1 className="text-2xl font-bold">My numbers</h1>
        {insights && insights.dayStreak > 0 && (
          <span className="text-sm font-medium text-amber-500">🔥 {insights.dayStreak}-day streak</span>
        )}
      </div>

      {loading && <p className="text-sm text-muted-foreground py-8 text-center">Loading...</p>}

      {!loading && summary && summary.questionsCompleted === 0 && (
        <MagnumSays>
          Nothing logged in this window yet. Run a round and I'll start keeping score.
        </MagnumSays>
      )}

      {!loading && insights && summary && summary.questionsCompleted > 0 && (
        <>
          <MagnumSays>{headline(insights)}</MagnumSays>

          <ActSummaryTiles summary={summary} />

          <section>
            <h2 className="text-lg font-semibold mb-1">Pace vs. the real clock</h2>
            <p className="text-xs text-muted-foreground mb-3">
              Your average per question against what the section actually gives you.
            </p>
            <ActPaceTable pace={insights.pace} />
          </section>

          {insights.strategies.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold mb-1">Moves that land</h2>
              <p className="text-xs text-muted-foreground mb-3">
                How often you get it right when you lead with each one.
              </p>
              <ActStrategyTable strategies={insights.strategies} />
            </section>
          )}

          {summary.predictionQuestions > 0 && (
            <section>
              <h2 className="text-lg font-semibold mb-1">Reading yourself</h2>
              <p className="text-xs text-muted-foreground mb-3">
                When you called it going in, how it actually went.
              </p>
              <CalibrationGrid summary={summary} />
            </section>
          )}
        </>
      )}

      {sessions.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold mb-2">Round by round</h2>
          <div className="space-y-2">
            {sessions.map(row => (
              <div key={row.sessionId} className="border border-border rounded-xl overflow-hidden">
                <button
                  onClick={() => openSessionDetail(row.sessionId)}
                  className="w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-accent transition min-h-[44px]"
                >
                  <div>
                    <p className="font-medium text-sm">{new Date(row.startedAt).toLocaleDateString()}</p>
                    <p className="text-xs text-muted-foreground">
                      {row.summary.questionsCompleted} questions · {percent(row.summary.accuracy)} right ·{' '}
                      {formatDuration(row.summary.avgActualSeconds)} apiece
                    </p>
                  </div>
                  <span className="text-muted-foreground text-sm">{openSession === row.sessionId ? '▲' : '▼'}</span>
                </button>
                {openSession === row.sessionId && (
                  <div className="p-3 border-t border-border bg-muted/30">
                    {sessionReview
                      ? <ActLogList logs={sessionReview.logs} catalog={catalog} />
                      : <p className="text-sm text-muted-foreground">Loading...</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
